import { z } from 'zod';
import { CheckoutField, TextKey } from '@mews-distributor/common';
import { Validation } from '@mews/framework';
import { CardNumberEventData, CvvEventData } from '@mews/framework/payments';
import { PaymentCardType } from '@mews/enums';

export const getCheckoutFormSchema = ({
    isPhoneRequired,
    isEmailRequired,
}: {
    isPhoneRequired: boolean;
    isEmailRequired: boolean;
}) =>
    z.object({
        [CheckoutField.Email]: isEmailRequired
            ? Validation.String.StrictEmail
            : Validation.String.StrictEmail.optional(),

        [CheckoutField.LastName]: Validation.String.Required,

        [CheckoutField.AgreeWithConditions]: z.literal(true),

        [CheckoutField.Telephone]: isPhoneRequired ? Validation.Number.Phone : Validation.Number.Phone.optional(),
    });

export const getBookerSchema = ({
    customerEmail,
    isPhoneRequired,
}: {
    customerEmail: string;
    isPhoneRequired: boolean;
}) =>
    z.object({
        [CheckoutField.BookerEmail]: Validation.String.StrictEmail.pipe(
            Validation.String.Exclusion({
                excludedValues: [customerEmail],
                textKey: TextKey.ValidateBookerEmail,
            })
        ),

        [CheckoutField.BookerLastName]: Validation.String.Required,

        [CheckoutField.Telephone]: isPhoneRequired ? Validation.Number.Phone : Validation.Number.Phone.optional(),
    });

export const getCreditCardFormSchema = (supportedCreditCardTypes: PaymentCardType[]) =>
    z.object({
        [CheckoutField.CardHolderName]: Validation.String.Required,
        [CheckoutField.CardExpiration]: Validation.PaymentCard.ExpiryDate,
        [CheckoutField.CardNumber]: Validation.PaymentCard.CardNumber.and(
            Validation.PaymentCard.CardType(supportedCreditCardTypes)
        ),
        [CheckoutField.CardCVV]: Validation.PaymentCard.Cvc,
    });

export const getPciProxyPaymentCardSchema = ({
    supportedCreditCardTypes,
    cardNumberMeta,
    cardCvvMeta,
}: {
    supportedCreditCardTypes: PaymentCardType[];
    cardNumberMeta: CardNumberEventData;
    cardCvvMeta: CvvEventData;
}) =>
    z.object({
        [CheckoutField.CardHolderName]: Validation.String.Required,
        [CheckoutField.CardExpiration]: Validation.PaymentCard.ExpiryDate,
        [CheckoutField.CardNumber]: Validation.PaymentCard.PciProxyValue({
            textKey: TextKey.ValidatePaymentCard,
            metadata: cardNumberMeta,
        }).and(
            Validation.PaymentCard.PciProxyCardTypeWithMetadata({
                supportedCardTypes: supportedCreditCardTypes,
                metadata: cardNumberMeta,
            })
        ),
        [CheckoutField.CardCVV]: Validation.PaymentCard.PciProxyValue({
            textKey: TextKey.ValidateCVV,
            metadata: cardCvvMeta,
        }),
    });
